/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import { Accordion, AccordionContent, AccordionTitle } from "../../ui/Accordion";
import Title from "../../ui/Title";
import CheckboxUncontrolled from "../../ui/form/CheckboxUncontrolled";
import Text from "../../ui/Text";
import T from "../../ui/Translation";
import Icon from "../../ui/Icon";
import LoadingIndicator from "../../LoadingIndicator";
import Flex from "../../ui/Flex";

const PackageInstanceItemTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const PackageIconCircle = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(${({ theme }) => theme.colors.background.rgba});
`;

const PackageContentItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  position: relative;
`;

const QtyText = styled(Text)`
  white-space: nowrap;
  flex-shrink: 0;
`;

const UsedBadge = styled.span`
  flex-shrink: 0;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.12);
  color: rgba(${({ theme }) => theme.colors.accent.rgba});
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  position: absolute;
  right: 0;
`;

const toQtyMap = (arr, idKey) => (arr || []).reduce((acc, x) => { acc[x[idKey]] = x.qty; return acc; }, {});

const ITEM_TYPES = [
  { pkgKey: "services",        instKey: "services",        idKey: "service_id", keyPrefix: "service",   icon: Icon.ServicePlaceholderIcon, iconStyle: { strokeWidth: 3 } },
  { pkgKey: "products",        instKey: "products",        idKey: "product_id", keyPrefix: "product",   icon: Icon.Bag,                    iconStyle: { strokeWidth: 5 } },
  { pkgKey: "paid_attributes", instKey: "paid_attributes", idKey: "product_id", keyPrefix: "paid_attr", icon: Icon.Gift,                   iconStyle: {}                 },
];

const StyledAccordion = styled(Accordion)`
  overflow: hidden;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 1rem;
  background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.04);

  > div + div {
    background: rgba(${({ theme }) => theme.colors.background.rgba});
    input[type="checkbox"] {
      background: rgba(${({ theme }) => theme.colors.background.rgba});
    }
  }
  input[type="checkbox"] {
    background: rgba(${({ theme }) => theme.colors.background.rgba});
  }
  
  ${({ theme }) => theme.containerMedia.sm} {
    box-shadow: none;
    border: none;
  }
`;

const PackageInstanceItem = ({ item, onSelect, isSelected, isLoading, isOpen, onToggle, readOnly, invoiceLines }) => {
  const itemPackage = item.package;

  const relevantServiceIds = new Set();
  const relevantProductIds = new Set();
  (invoiceLines || []).forEach((line) => {
    if ((line.qty ?? 0) - (line.package_qty ?? 0) === 0) return;
    (line.bookings || []).forEach((booking) => relevantServiceIds.add(booking.service_id));
    if (line.product_id) relevantProductIds.add(line.product_id);
  });

  const limitItem = +itemPackage.is_use_package_limit && +itemPackage.package_limit ? [{
    key:           "general_limit",
    name:          "General items limit",
    totalQty:      +itemPackage.package_limit,
    availableQty:  +itemPackage.package_limit - (item.count_used_package_instance || 0),
    usedQty:       0,
    matchesInvoice: false,
    icon:          Icon.FileList,
    iconStyle:     {},
  }] : [];

  const allItems = [
    ...limitItem,
    ...ITEM_TYPES.flatMap(({ pkgKey, instKey, idKey, keyPrefix, icon, iconStyle }) => {
      const available = toQtyMap(item[instKey], idKey);
      const used = toQtyMap(isSelected?.[instKey], idKey);
      const relevantIds = idKey === "service_id" ? relevantServiceIds : relevantProductIds;
      return (itemPackage[pkgKey] || []).map((x) => {
        const id = x[idKey];
        const availableQty = available[id] ?? 0;
        const usedQty = used[id] || 0;
        return {
          key:            `${keyPrefix}_${x.id}`,
          name:           x.name,
          totalQty:       x.qty,
          availableQty,
          usedQty,
          matchesInvoice: (availableQty > 0 && relevantIds.has(id)) || usedQty > 0,
          icon,
          iconStyle,
        };
      });
    }),
  ];

  // Selecting a package with no remaining qty on any item fails on the backend
  // with "This package has no applicable credits for the invoice items".
  const hasNoApplicableCredits = allItems.length > 0 && allItems.every((i) => i.availableQty <= 0);
  // When we know the invoice's lines, mirror the legacy widget: a package is only
  // usable if it still has qty on an item the invoice actually needs.
  const hasNoInvoiceMatch = Array.isArray(invoiceLines) && !allItems.some((i) => i.matchesInvoice);
  const isUnavailable = !isSelected && (!item.can_be_used || hasNoApplicableCredits || hasNoInvoiceMatch);

  const handleSelect = () => {
    if (isUnavailable) return;
    onSelect(item.id);
  };

  return (
    <StyledAccordion isOpen={isOpen} onToggle={onToggle}>
      <AccordionTitle>
        <PackageInstanceItemTitle>
          {readOnly
            ? <PackageIconCircle>
                <Icon.BoxArchive color="primary" size={Icon.SIZE.SM}/>
              </PackageIconCircle>
            : isLoading
              ? <LoadingIndicator size="md" />
              : <CheckboxUncontrolled
                  key={isSelected ? "selected" : "unselected"}
                  onChange={handleSelect}
                  onClick={(e) => e.stopPropagation()}
                  defaultValue={!!isSelected}
                  disabled={isUnavailable}
              />
          }
          {readOnly
            ? <Flex $direction="column">
                <Text>Applied packages</Text>
                <Text weight="bold" size="xlarge" color="dark">{itemPackage.name}</Text>
              </Flex>
            : <Title size="h3">{itemPackage.name}</Title>
          }
        </PackageInstanceItemTitle>
      </AccordionTitle>
      <AccordionContent>
        {allItems.map(({ key, name, totalQty, availableQty, usedQty, icon: ItemIcon, iconStyle }) => (
          <PackageContentItem key={key}>
            <ItemIcon size={16} style={iconStyle} color="iconStroke"/>
            <QtyText color="dark" weight="bold">
              {availableQty}  <T>of</T>  {totalQty}
            </QtyText>
            <div style={{width: '75%'}}><Text color="dark">{name}</Text></div>
            {!!isSelected && !!usedQty && <UsedBadge>  <T>Used</T> {usedQty}</UsedBadge>}
          </PackageContentItem>
        ))}
      </AccordionContent>
    </StyledAccordion>
  );
};

export default PackageInstanceItem;